import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, Info } from "lucide-react";

/**
 * BreastTissueAgeSection — Section 7 — educational metric showing how
 * the patient's metabolic/lifestyle environment compares to their
 * chronological age. NO ™ symbol (pending formal trademark registration).
 *
 * Includes:
 *  - dual-clock visual (chrono vs tissue)
 *  - per-factor contribution bar chart
 *  - interpretation text
 *  - confidence indicator
 */

const CONFIDENCE_BADGES = {
  high:   { bg: "bg-emerald-500/20", text: "text-emerald-300", label_bg: "Висока увереност", label_en: "High confidence", label_es: "Alta confianza" },
  medium: { bg: "bg-amber-500/20",   text: "text-amber-300",   label_bg: "Средна увереност", label_en: "Medium confidence", label_es: "Confianza media" },
  low:    { bg: "bg-slate-500/20",   text: "text-slate-300",   label_bg: "Ниска увереност",  label_en: "Low confidence",    label_es: "Baja confianza" },
};

const T = {
  bg: {
    chrono: "Хронологична възраст",
    tissue: "Възраст на средата",
    offset: "Разлика",
    interpretation: "Тълкуване",
    contributions: "Какво влияе",
    yearsOlder: "години по-възрастна",
    yearsYounger: "години по-млада",
    matched: "Съвпада",
    addData: "Добавете данни в профила, за да получите оценка.",
    disclaimer: "Образователен индекс. НЕ е измерване на тъкан. НЕ е клиничен показател за стареене.",
    older: "по-възрастна",
    younger: "по-млада",
    matchedDir: "съвпада",
  },
  en: {
    chrono: "Chronological age",
    tissue: "Environment age",
    offset: "Offset",
    interpretation: "Interpretation",
    contributions: "What's driving this",
    yearsOlder: "years older",
    yearsYounger: "years younger",
    matched: "Matched",
    addData: "Add profile data to receive an estimate.",
    disclaimer: "Educational metric. NOT a tissue measurement. NOT a clinical aging indicator.",
    older: "older",
    younger: "younger",
    matchedDir: "matched",
  },
  es: {
    chrono: "Edad cronológica",
    tissue: "Edad del entorno",
    offset: "Diferencia",
    interpretation: "Interpretación",
    contributions: "Qué lo impulsa",
    yearsOlder: "años más",
    yearsYounger: "años menos",
    matched: "Coincide",
    addData: "Añada datos al perfil para recibir una estimación.",
    disclaimer: "Métrica educativa. NO es una medida tisular. NO es un indicador clínico.",
    older: "más viejo",
    younger: "más joven",
    matchedDir: "coincide",
  },
};

export default function BreastTissueAgeSection({ tissueAge, cardCls, isDark, lang = "bg" }) {
  const t = T[lang] || T.bg;

  if (!tissueAge?.has_data) {
    return (
      <div className={`${cardCls} p-6 text-center`} data-testid="tissue-age-empty">
        <Info className="w-8 h-8 mx-auto opacity-40 mb-2" />
        <p className="text-sm opacity-70">{tissueAge?.note || t.addData}</p>
      </div>
    );
  }

  const { chronological_age, tissue_age, offset, direction, confidence, contributions, interpretation, disclaimer } = tissueAge;
  const ageDiff = Math.abs(offset);
  const conf = CONFIDENCE_BADGES[confidence] || CONFIDENCE_BADGES.medium;
  const confLabel = conf[`label_${lang}`] || conf.label_en;

  const DirectionIcon = direction === "older" ? TrendingUp : direction === "younger" ? TrendingDown : Minus;
  const directionColor = direction === "older" ? "#EF4444" : direction === "younger" ? "#22C55E" : "#9CA3AF";

  // Bar chart — top 8 contributions
  const topContribs = (contributions || []).slice(0, 8);
  const maxDelta = Math.max(1, ...topContribs.map((c) => Math.abs(c.delta)));

  return (
    <div className={`${cardCls} p-5 sm:p-6`} data-testid="tissue-age-card">
      {/* Top — dual clock visual */}
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        {/* Chrono */}
        <motion.div
          className="flex flex-col items-center p-4 rounded-2xl bg-gradient-to-br from-slate-500/10 to-slate-500/5"
          initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        >
          <div className="text-xs opacity-60 mb-1">{t.chrono}</div>
          <div className="text-5xl font-extrabold" data-testid="tissue-age-chrono">{chronological_age}</div>
        </motion.div>

        {/* Tissue */}
        <motion.div
          className="flex flex-col items-center p-4 rounded-2xl"
          style={{
            background: `linear-gradient(135deg, ${directionColor}22, ${directionColor}08)`,
            boxShadow: `0 0 30px ${directionColor}44`,
          }}
          initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="text-xs opacity-60 mb-1">{t.tissue}</div>
          <div className="text-5xl font-extrabold" style={{ color: directionColor }} data-testid="tissue-age-tissue">
            {tissue_age}
          </div>
        </motion.div>

        {/* Offset + confidence */}
        <motion.div
          className="flex flex-col items-center justify-center p-4 rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10"
          initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="text-xs opacity-60 mb-1">{t.offset}</div>
          <div className="flex items-center gap-1">
            <DirectionIcon className="w-5 h-5" style={{ color: directionColor }} />
            <div className="text-3xl font-extrabold" style={{ color: directionColor }} data-testid="tissue-age-offset">
              {offset > 0 ? "+" : ""}{offset}
            </div>
          </div>
          <div className="text-[10px] mt-1 opacity-70">
            {ageDiff === 0
              ? t.matched
              : direction === "older"
                ? `${ageDiff} ${t.yearsOlder}`
                : `${ageDiff} ${t.yearsYounger}`}
          </div>
          <span className={`mt-2 text-[10px] font-bold px-1.5 py-0.5 rounded ${
            isDark ? conf.bg + " " + conf.text : conf.bg.replace("500/20", "100").replace("dark:", "") + " " + conf.text.replace("300", "700")
          }`}>
            {confLabel}
          </span>
        </motion.div>
      </div>

      {/* Interpretation */}
      <div className={`p-3.5 rounded-xl mb-4 ${
        isDark ? "bg-pink-500/10 border border-pink-500/20" : "bg-pink-50 border border-pink-200"
      }`}>
        <div className="text-xs font-bold opacity-70 mb-1">{t.interpretation}</div>
        <p className="text-sm leading-snug" data-testid="tissue-age-interpretation">{interpretation}</p>
      </div>

      {/* Contributions bar chart */}
      {topContribs.length > 0 && (
        <div data-testid="tissue-age-contributions">
          <h4 className="text-xs font-bold opacity-70 mb-3">{t.contributions}</h4>
          <ul className="space-y-2">
            {topContribs.map((c, i) => {
              const positive = c.delta > 0;
              const widthPct = (Math.abs(c.delta) / maxDelta) * 100;
              return (
                <li key={i} className="flex items-center gap-3" data-testid={`tissue-age-factor-${i}`}>
                  <span className="text-xs w-28 sm:w-40 shrink-0 truncate" title={c.factor}>{c.factor}</span>
                  <div className="flex-1 relative h-5 rounded-md overflow-hidden bg-gray-100 dark:bg-white/5">
                    <motion.div
                      className="absolute top-0 bottom-0 rounded-md"
                      style={{
                        background: positive
                          ? "linear-gradient(90deg, #F87171, #EF4444)"
                          : "linear-gradient(90deg, #34D399, #22C55E)",
                        left: positive ? "50%" : `${50 - widthPct / 2}%`,
                        right: positive ? `${50 - widthPct / 2}%` : "50%",
                      }}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ delay: 0.05 * i, duration: 0.4 }}
                    />
                    <div className="absolute inset-y-0 left-1/2 w-px bg-gray-400/40" />
                  </div>
                  <span className={`text-xs font-bold w-10 text-right ${positive ? "text-red-500" : "text-emerald-500"}`}>
                    {positive ? "+" : ""}{c.delta}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-4 p-3 rounded-lg bg-amber-500/10 text-[11px] leading-snug flex items-start gap-2" data-testid="tissue-age-disclaimer">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
        <span>{disclaimer || t.disclaimer}</span>
      </div>
    </div>
  );
}
